import { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  DistributionChannel,
  DistributionChannelTypeEnum,
} from '../../../../api/queries';
import { useCreateNewDistributionChannelMutation } from '../../../../hooks/mutations/useCreateNewDistributionChannelMutation';
import { useInitiateTeamsOAuth } from '../../../../hooks/mutations/useInitiateTeamsOAuth';
import { useInitiateSlackOAuth } from '../../../../hooks/mutations/useInitiateSlackOAuth';
import { useInstallSlackChannel } from '../../../../hooks/mutations/useInstallSlackChannel';
import { useGetSlackWorkspaces } from '../../../../hooks/queries/useGetSlackWorkspaces';
import { GetDistributionChannelsCacheKey } from '../../../../hooks/queries';
import { WizardField } from '../../WizardField/WizardField';
import classes from './AddDistributionChannelCompact.module.css';

const TOAST_POSITION = 'bottom-right' as const;
const OAUTH_CONTEXT = 'create-survey' as const;

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;
const PHONE_PATTERN = /^\+?\d{8,15}$/;

type AddDistributionChannelCompactProps = {
  teamId: number;
  existingChannels?: DistributionChannel[];
  onChannelAdded?: () => void;
};

type ChannelErrors = Partial<Record<'email' | 'sms' | 'webhook', string>>;

type ChannelCardProps = {
  initial: string;
  title: string;
  description: string;
  children: React.ReactNode;
};

const ChannelCard = ({ initial, title, description, children }: ChannelCardProps) => (
  <li className={classes.card}>
    <div className={classes.cardHeader}>
      <span aria-hidden="true" className={classes.cardIcon}>
        {initial}
      </span>
      <span className={classes.cardTitle}>
        <h3 className="ds-heading" data-size="2xs">
          {title}
        </h3>
        <span className={`ds-paragraph ${classes.subtle}`} data-size="sm">
          {description}
        </span>
      </span>
    </div>
    {children}
  </li>
);

export const AddDistributionChannelCompact = ({
  teamId,
  existingChannels = [],
  onChannelAdded = () => {},
}: AddDistributionChannelCompactProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<ChannelErrors>({});
  const [selectedSlackWorkspace, setSelectedSlackWorkspace] = useState<
    number | null
  >(null);
  const [selectedSlackChannel, setSelectedSlackChannel] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [smsValue, setSmsValue] = useState('');
  const [webhookType, setWebhookType] = useState<DistributionChannelTypeEnum>(
    DistributionChannelTypeEnum.Slack,
  );
  const [webhookValue, setWebhookValue] = useState('');

  const {
    mutate: createNewDistributionChannel,
    isPending: isCreatingChannel,
  } = useCreateNewDistributionChannelMutation(teamId);
  const { mutate: initiateTeamsOAuth, isPending: isInitiatingTeamsOAuth } =
    useInitiateTeamsOAuth();
  const { mutate: initiateSlackOAuth, isPending: isInitiatingSlackOAuth } =
    useInitiateSlackOAuth();
  const { mutate: installSlackChannel, isPending: isInstallingSlackChannel } =
    useInstallSlackChannel();
  const { data: slackWorkspacesData, isLoading: isLoadingSlackWorkspaces } =
    useGetSlackWorkspaces();

  const workspaces = slackWorkspacesData?.workspaces ?? [];
  const hasSlackWorkspaces = !isLoadingSlackWorkspaces && workspaces.length > 0;
  // With a single workspace there is nothing to choose, so use it directly
  const activeWorkspaceId =
    selectedSlackWorkspace ?? (workspaces.length === 1 ? workspaces[0].id : null);
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);

  const slackChannelOptions = activeWorkspace
    ? [
        ...activeWorkspace.channels
          .filter((c) => !c.isPrivate)
          .map((channel) => ({ label: `#${channel.name}`, value: channel.id })),
        ...activeWorkspace.channels
          .filter((c) => c.isPrivate)
          .map((channel) => ({ label: `🔒 ${channel.name}`, value: channel.id })),
      ]
    : [];

  const setError = (key: keyof ChannelErrors, message?: string) =>
    setErrors((prev) => ({ ...prev, [key]: message }));

  const handleTeamsConnect = () => {
    if (!teamId) {
      toast.error(t('surveyCreation.distributionChannels.teams.missingTeamId'), {
        position: TOAST_POSITION,
      });
      return;
    }

    initiateTeamsOAuth(
      { teamId },
      {
        onSuccess: (data) => {
          sessionStorage.setItem('teams_oauth_teamId', String(teamId));
          sessionStorage.setItem('teams_oauth_context', OAUTH_CONTEXT);
          window.location.href = data.authorizationUrl;
        },
        onError: () => {
          toast.error(t('surveyCreation.distributionChannels.teams.errorConnecting'), {
            position: TOAST_POSITION,
          });
        },
      },
    );
  };

  const handleSlackConnect = () => {
    initiateSlackOAuth(undefined, {
      onSuccess: (data) => {
        sessionStorage.setItem('slack_oauth_teamId', String(teamId));
        sessionStorage.setItem('slack_oauth_context', OAUTH_CONTEXT);
        window.location.href = data.authorizationUrl;
      },
      onError: () => {
        toast.error(t('surveyCreation.distributionChannels.slack.errorConnecting'), {
          position: TOAST_POSITION,
        });
      },
    });
  };

  const handleAddSlackChannel = () => {
    if (!selectedSlackChannel || !activeWorkspaceId || !teamId) return;

    installSlackChannel(
      {
        trustpulseTeamId: teamId,
        slackChannelId: selectedSlackChannel,
        slackWorkspaceId: activeWorkspaceId,
      },
      {
        onSuccess: (data) => {
          setSelectedSlackChannel('');
          setSelectedSlackWorkspace(null);
          queryClient.invalidateQueries({
            queryKey: [GetDistributionChannelsCacheKey.GetDistributionChannels, teamId],
          });
          toast.success(
            t('surveyCreation.distributionChannels.slack.channelAdded', {
              channelName: data.channelName,
            }),
            { position: TOAST_POSITION },
          );
          onChannelAdded();
        },
        onError: () => {
          toast.error(t('surveyCreation.distributionChannels.slack.errorAdding'), {
            position: TOAST_POSITION,
          });
        },
      },
    );
  };

  const addChannel = (
    errorKey: keyof ChannelErrors,
    type: DistributionChannelTypeEnum,
    value: string,
    messages: { missing: string; invalid?: string; duplicate: string; success: string },
    isValid: (value: string) => boolean,
    clearValue: () => void,
  ) => {
    if (!value) {
      setError(errorKey, messages.missing);
      return;
    }
    if (!isValid(value)) {
      setError(errorKey, messages.invalid);
      return;
    }

    const duplicate = existingChannels.find(
      (channel) =>
        channel.type === type &&
        channel.webhookUrl?.toLowerCase() === value.toLowerCase(),
    );
    if (duplicate) {
      setError(errorKey, messages.duplicate);
      return;
    }

    createNewDistributionChannel(
      { type, webhookUrl: value },
      {
        onSuccess: () => {
          clearValue();
          toast.success(messages.success, { position: TOAST_POSITION });
          onChannelAdded();
        },
        onError: () => {
          toast.error(t('common.errors.defaultError'), { position: TOAST_POSITION });
        },
      },
    );
  };

  const handleAddEmail = () =>
    addChannel(
      'email',
      DistributionChannelTypeEnum.Email,
      emailValue.trim(),
      {
        missing: t('surveyCreation.distributionChannels.email.errorMissing'),
        invalid: t('surveyCreation.distributionChannels.email.errorInvalid'),
        duplicate: t('surveyCreation.distributionChannels.email.errorDuplicate'),
        success: t('surveyCreation.distributionChannels.email.successAdded'),
      },
      (value) => EMAIL_PATTERN.test(value),
      () => setEmailValue(''),
    );

  const handleAddSMS = () =>
    addChannel(
      'sms',
      DistributionChannelTypeEnum.SMS,
      smsValue.replace(/\s/g, ''),
      {
        missing: t('surveyCreation.distributionChannels.sms.errorMissing'),
        invalid: t('surveyCreation.distributionChannels.sms.errorInvalid'),
        duplicate: t('surveyCreation.distributionChannels.sms.errorDuplicate'),
        success: t('surveyCreation.distributionChannels.sms.successAdded'),
      },
      (value) => PHONE_PATTERN.test(value),
      () => setSmsValue(''),
    );

  const handleAddWebhook = () =>
    addChannel(
      'webhook',
      webhookType,
      webhookValue.trim(),
      {
        missing: t('surveyCreation.distributionChannels.advanced.errorMissing'),
        duplicate: t('surveyCreation.distributionChannels.advanced.errorDuplicate'),
        success: t('surveyCreation.distributionChannels.advanced.successAdded'),
      },
      () => true,
      () => setWebhookValue(''),
    );

  return (
    <div className={classes.container}>
      <ul className={classes.grid}>
        <ChannelCard
          initial="S"
          title={t('surveyCreation.distributionChannels.slack.title')}
          description={
            hasSlackWorkspaces
              ? t('surveyCreation.distributionChannels.slack.descriptionWithWorkspace')
              : t('surveyCreation.distributionChannels.slack.descriptionNoWorkspace')
          }
        >
          {hasSlackWorkspaces ? (
            <div className={classes.cardContent}>
              <p className={`ds-paragraph ${classes.connected}`} data-size="sm">
                <span className="ds-tag" data-size="sm" data-color="success">
                  {t('surveyCreation.distributionChannels.slack.connected')}
                </span>
                {workspaces.map((w) => w.workspaceName).join(', ')}
              </p>
              {workspaces.length > 1 && (
                <WizardField
                  id="slack-workspace"
                  size="sm"
                  label={t('surveyCreation.distributionChannels.slack.workspace')}
                >
                  {(control) => (
                    <select
                      {...control}
                      className="ds-input"
                      value={selectedSlackWorkspace?.toString() ?? ''}
                      onChange={(e) => {
                        setSelectedSlackWorkspace(
                          e.target.value ? Number(e.target.value) : null,
                        );
                        setSelectedSlackChannel('');
                      }}
                    >
                      <option value="">
                        {t('surveyCreation.distributionChannels.slack.selectWorkspace')}
                      </option>
                      {workspaces.map((workspace) => (
                        <option key={workspace.id} value={workspace.id}>
                          {workspace.workspaceName}
                        </option>
                      ))}
                    </select>
                  )}
                </WizardField>
              )}
              {activeWorkspace && (
                <WizardField
                  id="slack-channel"
                  size="sm"
                  label={t('surveyCreation.distributionChannels.slack.channel')}
                >
                  {(control) => (
                    <select
                      {...control}
                      className="ds-input"
                      value={selectedSlackChannel}
                      onChange={(e) => setSelectedSlackChannel(e.target.value)}
                    >
                      <option value="">
                        {t('surveyCreation.distributionChannels.slack.selectChannelPlaceholder')}
                      </option>
                      {slackChannelOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </WizardField>
              )}
            </div>
          ) : (
            <p className={`ds-paragraph ${classes.subtle}`} data-size="sm">
              {t('surveyCreation.distributionChannels.slack.redirectInfo')}
            </p>
          )}
          {hasSlackWorkspaces ? (
            <button
              type="button"
              className="ds-button"
              data-variant="secondary"
              onClick={handleAddSlackChannel}
              disabled={
                !selectedSlackChannel || !activeWorkspaceId || isInstallingSlackChannel
              }
            >
              {t('surveyCreation.distributionChannels.slack.add')}
            </button>
          ) : (
            <button
              type="button"
              className="ds-button"
              data-variant="secondary"
              onClick={handleSlackConnect}
              disabled={isInitiatingSlackOAuth || isLoadingSlackWorkspaces}
            >
              {t('surveyCreation.distributionChannels.slack.connect')}
            </button>
          )}
        </ChannelCard>

        <ChannelCard
          initial="M"
          title={t('surveyCreation.distributionChannels.teams.title')}
          description={t('surveyCreation.distributionChannels.teams.description')}
        >
          <p className={`ds-paragraph ${classes.subtle}`} data-size="sm">
            {t('surveyCreation.distributionChannels.teams.redirectInfo')}
          </p>
          <button
            type="button"
            className="ds-button"
            data-variant="secondary"
            onClick={handleTeamsConnect}
            disabled={isInitiatingTeamsOAuth}
          >
            {t('surveyCreation.distributionChannels.teams.connect')}
          </button>
        </ChannelCard>

        <ChannelCard
          initial="E"
          title={t('surveyCreation.distributionChannels.email.title')}
          description={t('surveyCreation.distributionChannels.email.description')}
        >
          <WizardField
            id="channel-email"
            size="sm"
            label={t('surveyCreation.distributionChannels.email.label')}
            error={errors.email}
          >
            {(control) => (
              <input
                {...control}
                className="ds-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={emailValue}
                onChange={(e) => {
                  setEmailValue(e.target.value);
                  setError('email', undefined);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddEmail();
                  }
                }}
              />
            )}
          </WizardField>
          <button
            type="button"
            className="ds-button"
            data-variant="secondary"
            onClick={handleAddEmail}
            disabled={isCreatingChannel}
          >
            {t('surveyCreation.distributionChannels.email.add')}
          </button>
        </ChannelCard>

        <ChannelCard
          initial="S"
          title={t('surveyCreation.distributionChannels.sms.title')}
          description={t('surveyCreation.distributionChannels.sms.description')}
        >
          <WizardField
            id="channel-sms"
            size="sm"
            label={t('surveyCreation.distributionChannels.sms.label')}
            error={errors.sms}
          >
            {(control) => (
              <input
                {...control}
                className="ds-input"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={smsValue}
                onChange={(e) => {
                  setSmsValue(e.target.value);
                  setError('sms', undefined);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSMS();
                  }
                }}
              />
            )}
          </WizardField>
          <button
            type="button"
            className="ds-button"
            data-variant="secondary"
            onClick={handleAddSMS}
            disabled={isCreatingChannel}
          >
            {t('surveyCreation.distributionChannels.sms.add')}
          </button>
        </ChannelCard>
      </ul>

      <details className="ds-details">
        <summary>{t('surveyCreation.distributionChannels.advanced.title')}</summary>
        <div className={classes.advanced}>
          <p className="ds-paragraph" data-size="sm">
            {t('surveyCreation.distributionChannels.advanced.description')}
          </p>
          <div className={classes.advancedFields}>
            <WizardField
              id="webhook-type"
              size="sm"
              label={t('surveyCreation.distributionChannels.advanced.webhookType')}
            >
              {(control) => (
                <select
                  {...control}
                  className="ds-input"
                  value={webhookType}
                  onChange={(e) =>
                    setWebhookType(e.target.value as DistributionChannelTypeEnum)
                  }
                >
                  <option value={DistributionChannelTypeEnum.Slack}>
                    {t('surveyCreation.distributionChannels.channels.slack')}
                  </option>
                  <option value={DistributionChannelTypeEnum.MicrosoftTeams}>
                    {t('surveyCreation.distributionChannels.channels.teams')}
                  </option>
                  <option value={DistributionChannelTypeEnum.Email}>
                    {t('surveyCreation.distributionChannels.channels.email')}
                  </option>
                  <option value={DistributionChannelTypeEnum.SMS}>
                    {t('surveyCreation.distributionChannels.channels.sms')}
                  </option>
                </select>
              )}
            </WizardField>
            <WizardField
              id="webhook-url"
              size="sm"
              label={t('surveyCreation.distributionChannels.advanced.webhookUrl')}
              error={errors.webhook}
            >
              {(control) => (
                <input
                  {...control}
                  className="ds-input"
                  type="url"
                  inputMode="url"
                  placeholder="https://"
                  value={webhookValue}
                  onChange={(e) => {
                    setWebhookValue(e.target.value);
                    setError('webhook', undefined);
                  }}
                />
              )}
            </WizardField>
          </div>
          <div>
            <button
              type="button"
              className="ds-button"
              data-variant="secondary"
              data-size="sm"
              onClick={handleAddWebhook}
              disabled={isCreatingChannel}
            >
              {t('surveyCreation.distributionChannels.advanced.add')}
            </button>
          </div>
        </div>
      </details>
    </div>
  );
};
